import { useProfileQuery } from './useProfile/useProfileQuery';
import { useProfileMutations } from './useProfile/useProfileMutations';
import type { UserProfile } from "@/types/profile";
import { useErrorTracking } from './useErrorTracking';
import { useLogProfileActivity, useCreateProfileSnapshot } from './useProfileActivity';
import { useAuth } from '@/contexts/AuthContext';

const SENSITIVE_FIELDS = new Set([
  'personal_email',
  'personal_phone',
  'business_phone',
]);

export function useProfileState() {
  const { data: profile, isLoading, error } = useProfileQuery();
  const { updateProfile: updateProfileMutation, isUpdating } = useProfileMutations();
  const { captureError } = useErrorTracking();
  const logActivity = useLogProfileActivity();
  const createSnapshot = useCreateProfileSnapshot();
  const { user } = useAuth();

  // Return current profile data or empty profile structure if loading/no data
  const currentProfile = profile || ({
    legal_first_name: "",
    legal_last_name: "",
    business_email: "",
  } as UserProfile);

  /**
   * Save a delta of changed profile fields.
   *
   * - One supabase profiles.update() per call.
   * - One create_profile_snapshot() call before the update if business_email is in the delta.
   * - One log_profile_activity() call per changed field, fired in parallel after the update.
   *   Activity logging failures are captured to Sentry but never surfaced to the user.
   *
   * @param delta              - Object containing only the fields that changed
   * @param previousValues     - Snapshot of the previous values for the same fields (for activity log old_value)
   */
  const saveProfileDelta = async (
    delta: Partial<UserProfile>,
    previousValues: Partial<UserProfile> = {},
  ) => {
    const profileUserId = user?.id;
    const changedFields = Object.keys(delta) as Array<keyof UserProfile>;

    if (changedFields.length === 0) return;

    // Snapshot before mutating business_email (sensitive identity field).
    if (profileUserId && 'business_email' in delta) {
      try {
        await createSnapshot.mutateAsync({
          profileUserId,
          changeSummary: 'Updated business email',
        });
      } catch (snapshotError) {
        // Don't block the save on snapshot failure — log and continue.
        captureError(snapshotError as Error, {
          extra: { operation: 'createSnapshot', delta: Object.keys(delta) },
        });
      }
    }

    // Single profiles.update() for the whole delta.
    await updateProfileMutation(delta as Parameters<typeof updateProfileMutation>[0]);

    // Fire activity log entries in parallel — one per changed field.
    // Failures here MUST NOT cause the user-visible save to appear failed.
    if (profileUserId) {
      const activityResults = await Promise.allSettled(
        changedFields.map((field) =>
          logActivity.mutateAsync({
            profileUserId,
            actionType: 'update',
            fieldName: field as string,
            oldValue: previousValues[field],
            newValue: delta[field],
            isSensitive: SENSITIVE_FIELDS.has(field as string),
          }),
        ),
      );

      activityResults.forEach((result, idx) => {
        if (result.status === 'rejected') {
          captureError(result.reason as Error, {
            extra: {
              operation: 'logProfileActivity',
              field: changedFields[idx],
            },
          });
        }
      });
    }
  };

  /**
   * @deprecated Use `saveProfileDelta` from sections that batch user edits.
   * Kept for the profile image flow which is intentionally save-on-upload.
   */
  const updateProfile = async (field: keyof UserProfile, value: any) => {
    const oldValue = currentProfile[field];
    await saveProfileDelta(
      { [field]: value } as Partial<UserProfile>,
      { [field]: oldValue } as Partial<UserProfile>,
    );
  };

  const resetProfile = () => {
    // No-op: section components own their local state and Cancel reverts there.
  };

  return {
    profile: currentProfile,
    saveProfileDelta,
    updateProfile,
    resetProfile,
    isLoading,
    isSaving: isUpdating,
    error,
  };
}
